import { useContext, useEffect, useMemo, useState, useRef } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import type { AdditionalDetailsData, ResultCode, UIElement } from '@adyen/adyen-web';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { adyenService } from '@/services/adyenService';
import { adyenExpressService } from '@/services/adyenExpressService';
import { adyenApplePayService } from '@/services/adyenApplePayService';
import { ADYEN_3DS_CONTAINER_ID, ADYEN_3DS_ORDER_POLL_INTERVAL_MS } from '@/constants/adyen';
import type { AdyenFinalizePaymentResponse } from '@/types/adyen';
import { useGetStatus } from '@/api/status';
import { useGetOrder } from '@/api/order';
import { ChargeStates } from '@/types/order';
import { ErrorLogs, InfoLogs } from '@/constants/logs';
import { logError, logInfo } from '@/utils/logger';
import {
  captureRuntimeEnvironment,
  describeChallengeGeometry,
  describeChallengeIssuer,
  isChallengeFocused,
  startLivenessHeartbeat,
  watchPageLifecycle,
} from '@/utils/paymentDiagnostics';
import { consumeCardActions, getAdyenConfig } from '@/utils/adyen';
import { clearPendingChallenge, savePendingChallenge } from '@/utils/pendingChallengeStore';
import { FeatureFlag } from '@/constants/featureFlags';
import { useTranslate } from '@/hooks/useTranslate';

export const AdyenCard3DSForm = () => {
  const [adyenComponent, setAdyenComponent] = useState<UIElement | null>(null);
  const {
    customer,
    paymentMethodsSettings,
    paymentMethodSelected,
    featureFlags,
    threeDSSecurityState: { params, provider, paymentMethod, handlePaymentSuccess, handlePaymentError },
    actions: { setThreeDSSecurityState },
    card: { adyenCardDetailsRef },
  } = useContext(CheckoutContext);
  const getStatus = useGetStatus();
  const getOrder = useGetOrder();
  const { t } = useTranslate();
  const isInitializedRef = useRef(false);
  const componentRef = useRef<UIElement | null>(null);
  /**
   * Separates the two ways the challenge effect below can be cleaned up, which
   * mean opposite things. A dependency change tears the challenge down and
   * immediately rebuilds it — survivable, but it re-sends the CReq. An unmount
   * ends the payment: nothing is left that could report an outcome. Both run
   * the same cleanup, so without this they are one indistinguishable log.
   *
   * Declared before that effect on purpose. React runs cleanups in the order
   * their effects were defined, so on unmount this one sets the flag first and
   * the challenge cleanup reads it; on a dependency change it never runs.
   */
  const isUnmountingRef = useRef(false);
  useEffect(() => () => void (isUnmountingRef.current = true), []);

  // Express GPay/ApplePay don't update context's `paymentMethodSelected`,
  // so prefer the method threaded through `threeDSSecurityState`.
  const activeMethod = paymentMethod ?? paymentMethodSelected;

  const isChallengeResumeEnabled = featureFlags[FeatureFlag.ADYEN_3DS_CHALLENGE_RESUME] === true;

  // Resolve the polling `paymentMethodId` against the *active* method
  // (cards or wallets). Was previously hardcoded to CREDIT_CARD which
  // broke status polling for GPay / ApplePay 3DS (`/status` returned no
  // resultCode → component never resolved → spinner forever).
  const paymentMethodId = useMemo(() => {
    const resolvedMethod = activeMethod ?? PaymentMethodEnum.CREDIT_CARD;
    return (
      paymentMethodsSettings.find(
        (method) => method.paymentMethodName === resolvedMethod && method.provider === ProviderEnum.ADYEN
      )?.id ??
      // Defensive fallback to the card method id in case the wallet's
      // settings entry is missing (shouldn't happen post-boot).
      paymentMethodsSettings.find(
        (method) => method.paymentMethodName === PaymentMethodEnum.CREDIT_CARD && method.provider === ProviderEnum.ADYEN
      )?.id ??
      ''
    );
  }, [paymentMethodsSettings, activeMethod]);

  useEffect(() => {
    if (adyenComponent) {
      adyenComponent.mount(document.getElementById(ADYEN_3DS_CONTAINER_ID) as HTMLElement);
    }
  }, [adyenComponent]);

  useEffect(() => {
    if (!params || provider !== ProviderEnum.ADYEN) {
      isInitializedRef.current = false;
      return;
    }

    if (isInitializedRef.current) return;

    isInitializedRef.current = true;

    if (handlePaymentSuccess) {
      adyenService.updateOnPaymentSuccess(handlePaymentSuccess);
    }
    if (handlePaymentError) {
      adyenService.updateOnPaymentError(handlePaymentError);
    }

    // The single exit for a challenge, whatever ended it.
    //
    // When the card advanced flow still owns live SubmitActions the outcome has
    // to travel back through them: the SDK's own failure path never resets the
    // element, so `onPaymentFailed` — where `setStatus('ready')` lives — is the
    // only thing that frees the card form for a retry. `reject()` carries no
    // payload, hence handing the reason to the service first.
    //
    // Taking the actions off the ref matters as much as using them: a spent
    // handle left over from an earlier payment would absorb this settle with no
    // effect, and the shopper would be left under the in-progress blocker with
    // nothing reported.
    //
    // `isSettled` holds the other half of that invariant. Adyen can fire
    // `onAdditionalDetails` more than once for a single challenge, and `onError`
    // can arrive after an outcome has already been reported, so without it a
    // repeat would dispatch the payment a second time — a duplicate
    // `notifyPaymentIntentFailed`, or a success landing on top of a decline.
    // The flag is scoped to this effect, which runs once per challenge (the
    // form unmounts with the modal), so a later challenge starts clean.
    let isSettled = false;
    // Guards the /payment/status submission, not the outcome dispatch: it
    // flips synchronously on the first `onAdditionalDetails` so an SDK
    // duplicate cannot replay the POST while the first one is still awaited.
    let isChallengeResultSubmitted = false;

    // Liveness instruments for the pending window (ACDEV-26410). The
    // backgrounded/foregrounded pair below can only report a page the browser
    // let us say goodbye from; a killed page says nothing. The heartbeat is
    // delivered incrementally instead, so the last beat to arrive dates the end
    // of execution even when nothing else does — and stopping both on settle is
    // what makes "beats ran out" mean the challenge never finished.
    // Sticky: one confirmed touch is engagement for the rest of the challenge,
    // and focus will have moved on by the time the shopper leaves.
    let hasEngagedWithChallenge = false;
    // What the order-state poll below last saw. Carrying it on every beat is
    // what pairs client liveness with server truth: "page alive, order still
    // pending" and "page alive, order already paid and the challenge never
    // noticed" are different bugs that otherwise look the same.
    let lastOrderState = 'unpolled';
    // Dates the teardown below. A challenge killed a few seconds in is our own
    // re-render; one killed after minutes is a shopper who gave up.
    const challengeStartedAt = Date.now();

    const diagnosticContext = () => {
      hasEngagedWithChallenge = hasEngagedWithChallenge || isChallengeFocused(ADYEN_3DS_CONTAINER_ID);
      return { paymentMethodId, activeMethod, hasEngagedWithChallenge, lastOrderState };
    };
    const stopHeartbeat = startLivenessHeartbeat(InfoLogs.adyen3dsChallengeHeartbeat, diagnosticContext);
    const stopLifecycleWatch = watchPageLifecycle(InfoLogs.paymentPageLifecycle, () => ({
      ...diagnosticContext(),
      phaseContext: 'adyen_3ds_challenge',
    }));
    const stopDiagnostics = () => {
      stopHeartbeat();
      stopLifecycleWatch();
    };

    const settleChallenge = (resultCode?: ResultCode, rawReason?: string) => {
      if (isSettled) return;
      isSettled = true;
      stopDiagnostics();
      // However the challenge ended, it must not be resumable. This is the
      // single exit, so clearing here is what stops a completed payment being
      // re-mounted the next time the checkout boots.
      if (isChallengeResumeEnabled) clearPendingChallenge();

      const cardActions = consumeCardActions(adyenCardDetailsRef);
      if (cardActions) {
        if (resultCode) {
          // Resolving with a failed resultCode is deliberate: the SDK rejects
          // internally on Refused / Cancelled / Error and hands the code to
          // `onPaymentFailed`, so the reason reaches the error handler without
          // a second dispatch from here.
          cardActions.resolve({ resultCode });
        } else {
          adyenService.setPendingFailureReason(rawReason);
          cardActions.reject();
        }
      } else if (resultCode === 'Authorised') {
        adyenService.callOnPaymentSuccess({ resultCode });
      } else {
        adyenService.callOnPaymentError(undefined, rawReason ?? resultCode);
      }
    };

    // Nothing mounted, so no challenge callback will ever fire. Reporting the
    // failure here is what stops the shopper sitting on an empty modal until
    // the order expires, and it cannot double-dispatch for the same reason.
    const abandonChallenge = (rawReason: string, error?: unknown) => {
      logError(ErrorLogs.adyen3dsChallengeUnavailable, error, { paymentMethodId, activeMethod, reason: rawReason });
      settleWalletSheet(activeMethod, false);
      settleChallenge(undefined, rawReason);
      setThreeDSSecurityState({ show3DSForm: false });
    };

    // Declared ahead of `createComponent` because that runs synchronously up to
    // its first await, before the watchdog section below would have initialised
    // it. Reading it there in its temporal dead zone would throw and take the
    // whole challenge down.
    const orderId = (params as AdyenFinalizePaymentResponse).orderId;

    const createComponent = async () => {
      const action = (params as AdyenFinalizePaymentResponse).adyen.action!;
      logInfo(InfoLogs.adyen3dsChallengeMounted, {
        paymentMethodId,
        activeMethod,
        actionType: action.type,
        actionSubtype: (action as { subtype?: string }).subtype ?? 'none',
        // The one dimension that separates a Spanish challenge that never
        // settles from a French one that does. Joined to the outcome by order.
        ...describeChallengeIssuer(action as { token?: string }),
      });

      // Mirror the challenge outside the JS heap before it can be interrupted.
      // A discarded page takes the order-state watchdog with it, so this record
      // is the only thing the reloaded checkout can recover from.
      if (isChallengeResumeEnabled && orderId) {
        savePendingChallenge({ params, orderId, paymentMethodId, activeMethod });
      }

      const adyenConfig = getAdyenConfig(
        paymentMethodsSettings as unknown[],
        activeMethod ?? PaymentMethodEnum.CREDIT_CARD
      );
      if (adyenConfig) {
        await adyenService.initialize(adyenConfig, customer);
      }

      const AdyenComponent = await adyenService.createFromAction(action, {
        onAdditionalDetails: async (state: AdditionalDetailsData, _component, sdkActions) => {
          // Adyen fires this callback more than once for a single challenge
          // (the challenge iframe's result postMessage can be delivered
          // twice). Replaying the /payment/status POST is not idempotent
          // upstream: Adyen answers the second /payments/details for the
          // same payment with 422 "request already processed", which the
          // backend surfaced as a 500 — on a payment that was already PAID
          // (prod trace, ACDEV-26410). Submit the result exactly once; a
          // repeat only settles the SDK's own lifecycle. The flag is set
          // before the await, so a duplicate arriving while the first
          // request is still in flight is deduped too — `isSettled` alone
          // flips only after the response and misses that window.
          if (isChallengeResultSubmitted) {
            sdkActions?.reject();
            return;
          }
          isChallengeResultSubmitted = true;

          try {
            const { resultCode } = await getStatus.mutateAsync({
              paymentMethodId,
              sessionToken: state.data.details.threeDSResult ?? '',
            });

            // Adyen's advanced-flow contract: the SDK wraps this handler in a
            // promise and requires `actions.resolve({ resultCode })` after the
            // details call — even for a failed payment — or `actions.reject()`
            // on error. The challenge component has no onPaymentCompleted /
            // onPaymentFailed wired, so settling it only closes the SDK's
            // internal lifecycle (component state + final analytics event) and
            // cannot double-dispatch the outcome reported below.
            //
            // Version reality: the pinned SDK (6.23.0) invokes this handler
            // through the component's `onComplete` with two arguments, so
            // `sdkActions` is undefined and these settles no-op — 6.23.0 also
            // neither promise-wraps nor awaits this handler on the
            // createFromAction path, so nothing is left pending there either.
            // `createFromAction` only started passing `actions` in 6.24.0
            // (adyen-web#3589, usable from 6.25.1), where the SDK *does* wrap
            // this handler in a promise; from that version on, settling here
            // is mandatory or the challenge component hangs. This is what
            // keeps the eventual SDK upgrade from reintroducing a silent
            // pending-promise hang.
            if (resultCode) {
              sdkActions?.resolve({ resultCode });
            } else {
              sdkActions?.reject();
            }

            const isAuthorised = resultCode === 'Authorised';
            settleWalletSheet(activeMethod, isAuthorised, isAuthorised ? resultCode : undefined);

            // Both branches below are terminal for the challenge, so both must
            // log: the `actions` branch (card advanced flow) used to resolve
            // silently, which hid every card 3DS completion from RUM and made
            // the challenge funnel unmeasurable for the dominant payment method.
            if (isAuthorised) {
              logInfo(InfoLogs.adyen3dsPaymentSuccess, { paymentMethodId, resultCode, activeMethod });
            } else {
              // `resultCode` rides the log so an ordinary issuer decline
              // (Refused / Cancelled) is separable from a technical failure —
              // previously this object was passed in `logError`'s `errorData`
              // slot, which stringified it to "[object Object]" and dropped it.
              logError(ErrorLogs.adyen3dsPaymentError, undefined, {
                paymentMethodId,
                resultCode: resultCode ?? 'none',
                activeMethod,
              });
            }

            // Carry the resultCode out of the challenge: the handler's
            // fallback is the pre-challenge `requires_action` response, which
            // never holds a decline reason.
            settleChallenge(resultCode, resultCode ?? 'no_result_code');
          } catch (error) {
            // Per the same contract, a failed /payment/status maps to
            // `actions.reject()` so the SDK component does not wait forever.
            sdkActions?.reject();
            logError(ErrorLogs.adyen3dsStatusError, error, { paymentMethodId, activeMethod });
            settleWalletSheet(activeMethod, false);
            settleChallenge(undefined, 'status_request_failed');
          }
          setThreeDSSecurityState({ show3DSForm: false });
        },
        // Fires once the challenge iframe actually paints. Its absence in a
        // session that received a ChallengeShopper action is the signal that
        // the challenge never reached the shopper at all.
        onActionHandled: (rtnObj) => {
          // Geometry and environment ride this log because it is the one moment
          // the challenge is known to exist and the page is known to be healthy.
          // `onActionHandled` firing has been read as "the iframe renders fine",
          // but it only reports that the SDK handled the action — the geometry
          // fields are what distinguish a usable challenge from one that painted
          // at zero height or off-screen, and the environment fields give the
          // dying cohort something to be tested against.
          logInfo(InfoLogs.adyen3dsChallengePresented, {
            paymentMethodId,
            activeMethod,
            componentType: rtnObj.componentType,
            actionDescription: rtnObj.actionDescription,
            ...describeChallengeGeometry(ADYEN_3DS_CONTAINER_ID),
            ...captureRuntimeEnvironment(),
          });
        },
        // Outside the merchant-defined flow the SDK only reaches `onError`
        // on unrecoverable paths — a postMessage it cannot parse, or an
        // action with no `paymentData` / `authorisationToken`, where the
        // challenge renders nothing at all. None of them go on to call
        // `onAdditionalDetails`, so failing the payment here cannot
        // double-dispatch, and it replaces a modal that would otherwise hang
        // until the order expires.
        onError: (error) => {
          logError(ErrorLogs.adyen3dsComponentError, error, { paymentMethodId, activeMethod });
          settleWalletSheet(activeMethod, false);
          settleChallenge(undefined, 'challenge_component_error');
          setThreeDSSecurityState({ show3DSForm: false });
        },
      });

      if (!AdyenComponent) {
        abandonChallenge('challenge_unavailable');
        return;
      }

      componentRef.current = AdyenComponent as UIElement;
      setAdyenComponent(AdyenComponent as UIElement);
    };

    createComponent().catch((error) => abandonChallenge('challenge_setup_failed', error));

    // Order-state watchdog (ACDEV-26410). The dominant mobile 3DS failure is
    // an out-of-band challenge: the shopper app-switches to their bank app,
    // the OS backgrounds (or kills) this page, and the challenge result never
    // travels back through the iframe — while Adyen's AUTHORISATION webhook
    // settles the order server-side regardless. The webhook's outcome is
    // readable through GET /order, so poll it while the challenge is pending
    // and settle the checkout from it when it turns terminal. The shared
    // `isSettled` flag makes the two sources race-safe: whichever of
    // (challenge callbacks | watchdog) reports first wins, the other no-ops.
    let isOrderPollInFlight = false;

    const pollOrderState = async () => {
      if (isSettled || isOrderPollInFlight || !orderId) return;
      isOrderPollInFlight = true;
      try {
        const order = await getOrder.mutateAsync(orderId);
        lastOrderState = String(order.state);
        if (isSettled) return;
        if (order.state !== ChargeStates.SUCCESS && order.state !== ChargeStates.FAILED) return;

        const isPaid = order.state === ChargeStates.SUCCESS;
        logInfo(InfoLogs.adyen3dsSettledByOrderPoll, {
          paymentMethodId,
          activeMethod,
          orderState: order.state,
          visibilityState: document.visibilityState,
        });
        settleWalletSheet(activeMethod, isPaid, isPaid ? 'Authorised' : undefined);
        if (isPaid) {
          settleChallenge('Authorised');
        } else {
          settleChallenge(undefined, 'order_failed_during_challenge');
        }
        setThreeDSSecurityState({ show3DSForm: false });
      } catch {
        // A transient GET /order failure must never disturb a live challenge;
        // the next tick simply retries.
      } finally {
        isOrderPollInFlight = false;
      }
    };

    const orderPollTimer = setInterval(pollOrderState, ADYEN_3DS_ORDER_POLL_INTERVAL_MS);

    // Visibility tracking serves two purposes. Operationally: background tabs
    // get their timers throttled to minutes, so polling straight away on
    // return is what makes "come back from the bank app" feel instant.
    // Diagnostically: the backgrounded/foregrounded pair is the only way to
    // tell an app-switch (hidden fires, usually flushes before a webview
    // kill) from a page that died with no user action (nothing fires) — the
    // open question behind the mobile dead-session cohort (ACDEV-26410).
    const challengeMountedAt = Date.now();
    let hiddenAt: number | undefined;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        if (!isSettled) {
          hiddenAt = Date.now();
          logInfo(InfoLogs.adyen3dsChallengeBackgrounded, {
            paymentMethodId,
            activeMethod,
            msSinceChallengeMounted: Date.now() - challengeMountedAt,
          });
        }
        return;
      }

      if (hiddenAt !== undefined && !isSettled) {
        logInfo(InfoLogs.adyen3dsChallengeForegrounded, {
          paymentMethodId,
          activeMethod,
          hiddenDurationMs: Date.now() - hiddenAt,
        });
      }
      hiddenAt = undefined;
      pollOrderState();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      // Everything that could still report an outcome dies on the next four
      // lines: the poll that reads the webhook's verdict, the heartbeat that
      // dates the end of execution, and the Adyen component holding the
      // challenge. If the payment has not settled by now it never will, and
      // until this log existed that exit was completely dark — a challenge
      // that ends here looks in RUM exactly like a shopper who walked away.
      if (!isSettled) {
        logInfo(InfoLogs.adyen3dsChallengeTornDown, {
          paymentMethodId,
          activeMethod,
          orderId,
          lastOrderState,
          hasEngagedWithChallenge,
          msSinceChallengeStart: Date.now() - challengeStartedAt,
          visibilityState: document.visibilityState,
          // The whole point of the log. `unmounted` is a dead payment;
          // `deps_changed` is a challenge that gets rebuilt and re-sends its
          // CReq. Same cleanup, opposite meanings.
          reason: isUnmountingRef.current ? 'unmounted' : 'deps_changed',
        });
      }
      clearInterval(orderPollTimer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopDiagnostics();
      isInitializedRef.current = false;
      if (componentRef.current) {
        componentRef.current.unmount();
        componentRef.current = null;
      }
      setAdyenComponent(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, provider]);

  if (!params || provider !== ProviderEnum.ADYEN) return null;

  return (
    <div className="fixed inset-0 z-150 flex items-center justify-center bg-black/50">
      {/* `min(50rem,100dvh)` caps the modal at the *visual* viewport: on a
          landscape phone (~360-430px tall) the header + 400px container is
          taller than the screen, and with nothing scrollable the challenge's
          lower edge was simply unreachable. The container is the flex child
          that absorbs the shortfall (overflow-auto gives it a 0 automatic
          min-height), so the responsive '05' iframe shrinks with it while a
          fixed-size iframe becomes reachable by scrolling. */}
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-0 md:mx-4 pt-2 pb-2 flex flex-col max-h-[min(50rem,100dvh)]">
        <h2 className="text-xl font-semibold mb-4 w-full px-5 shrink-0">{t('securePaymentVerification')}</h2>
        {/* The height must be definite, not a min-height: the responsive
            challenge size ('05', used on mobile) sizes its iframe with
            `height: 100%`, which collapses to the 150px iframe default when the
            parent height is content-derived. 25rem matches the 400px the fixed
            sizes already occupy, so desktop rendering is unchanged. */}
        <div id={ADYEN_3DS_CONTAINER_ID} className="w-full h-[25rem] px-5 overflow-auto" />
      </div>
    </div>
  );
};

// Express wallets keep their native sheet open until the SubmitActions promise
// from the original `onSubmit` resolves or rejects. The card flow uses
// `adyenCardDetailsRef.actions` for this, but wallets store their actions on
// the dedicated wallet services. Without dispatching here the GPay / ApplePay
// sheet stays on screen after the challenge settles.
const settleWalletSheet = (activeMethod?: PaymentMethodEnum, isAuthorised = false, resultCode?: ResultCode): void => {
  if (activeMethod === PaymentMethodEnum.GOOGLE_PAY) {
    adyenExpressService.completeGooglePayPayment(isAuthorised, resultCode);
  } else if (activeMethod === PaymentMethodEnum.APPLE_PAY) {
    adyenApplePayService.completeApplePayPayment(isAuthorised, resultCode);
  }
};
